import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'validate.js';

import Icon from 'dpl/common/components/Icon';
import Input from 'dpl/common/design_system/Input';
import FancyDropdown, {
  FancyDropdownMenu,
  FancyDropdownMenuItem
} from 'dpl/components/FancyDropdown';

import { LocationResultsResultGoogleItem } from '../components/LocationResultsList';
import { removeCountryFromLocationName } from '../utils/helpers';

function formatPlace(place, options = {}) {
  if (options.stripCountryCode) {
    return { ...place, name: removeCountryFromLocationName(place) };
  }

  return place;
}

const locationAutocompleteDefaultPropClasses = {
  menu: null,
  menuContent: null,
  menuItem: null,
  selectedMenuItem: null
};
/** @param {any} props */
export default function LocationAutocomplete({
  classes = locationAutocompleteDefaultPropClasses,
  onSelect,
  onClear = null,
  onResultsChange = null,
  query,
  googlePlacesResults,
  inputProps,
  clearIconProps = null,
  menuPosition = 'left',
  menuCloseTimeout = 0,
  stripCountryCode = false,
  ResultsItemComponent = LocationResultsResultGoogleItem,
  newDesignSystemStyles = false,
  resultsItemProps = null
}) {
  const [isOpen, setIsOpen] = useState(false);

  const defaultInputRef = useRef();
  const blurTimeoutRef = useRef(null);
  const prevResultsCountRef = useRef(0);

  const inputRef = inputProps.ref || defaultInputRef;

  function handleInputFocus() {
    setIsOpen(true);
    inputProps.onFocus?.();
  }

  function handleInputBlur() {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      inputProps.onBlur?.();
    }, menuCloseTimeout);
  }

  function handleInputClear() {
    onClear();
    inputRef.current?.focus?.();
  }

  const hasClearButton = typeof onClear === 'function' && !isEmpty(query);

  const inputStyle = useMemo(() => {
    if (!hasClearButton) return null;
    return { ...inputProps.style, paddingRight: '40px' };
  }, [hasClearButton, inputProps.style]);

  useEffect(() => {
    const currentResultsLength = googlePlacesResults?.length || 0;
    const prevResultsLength = prevResultsCountRef.current;

    // Only open menu when transitioning from no results to having results.
    // This prevents reopening when results already exist.
    if (
      prevResultsLength === 0 &&
      currentResultsLength > 0 &&
      inputRef.current === document.activeElement
    ) {
      setIsOpen(true);
    }

    prevResultsCountRef.current = currentResultsLength;
    onResultsChange?.(googlePlacesResults);
  }, [googlePlacesResults, onResultsChange]);

  useEffect(() => {
    // Clean up blur timeout on unmount
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="LocationAutocomplete">
      <FancyDropdown
        onChange={setIsOpen}
        isOpen={isOpen}
        menuPosition={menuPosition}
      >
        <div className="relative flex items-center">
          <Input
            {...inputProps}
            ref={inputRef}
            style={inputStyle}
            type="text"
            autoComplete="off"
            value={query}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
          />
          {hasClearButton && (
            <button
              type="button"
              className="absolute top-0 right-0 bottom-0 br3 overflow-hidden ph3"
              aria-label="Clear"
              onClick={handleInputClear}
            >
              <Icon
                name="fetch-close"
                height="24px"
                width="24px"
                {...clearIconProps}
              />
            </button>
          )}
        </div>
        {!isEmpty(googlePlacesResults) && (
          <div role="presentation" onMouseDown={e => e.preventDefault()}>
            <FancyDropdownMenu
              className={classnames(
                'bg-white overflow-y-scroll no-scrollbars overflow-x-hidden',
                classes.menu
              )}
              contentClassName={classes.menuContent}
              newDesignSystemStyles={newDesignSystemStyles}
            >
              {googlePlacesResults.map((result, index) => {
                const place = formatPlace(result, { stripCountryCode });

                return (
                  <FancyDropdownMenuItem
                    key={`${place.name}-${index}`}
                    itemKey={place.name}
                    className={classes.menuItem}
                    selectedClassName={classes.selectedMenuItem}
                  >
                    <ResultsItemComponent
                      {...resultsItemProps}
                      place={place}
                      onClick={onSelect}
                    />
                  </FancyDropdownMenuItem>
                );
              })}
            </FancyDropdownMenu>
          </div>
        )}
      </FancyDropdown>
    </div>
  );
}

LocationAutocomplete.propTypes = {
  classes: PropTypes.shape({
    menu: PropTypes.string,
    menuContent: PropTypes.string,
    menuItem: PropTypes.string,
    selectedMenuItem: PropTypes.string
  }),
  menuPosition: PropTypes.oneOf(['left', 'right', 'static']),
  menuCloseTimeout: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  query: PropTypes.string.isRequired,
  googlePlacesResults: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      getCoordinates: PropTypes.func.isRequired
    })
  ).isRequired,
  inputProps: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    style: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    ref: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func
  }).isRequired,
  clearIconProps: PropTypes.shape({
    className: PropTypes.string
  }),
  onResultsChange: PropTypes.func,
  stripCountryCode: PropTypes.bool,
  ResultsItemComponent: PropTypes.func,
  newDesignSystemStyles: PropTypes.bool,
  resultsItemProps: PropTypes.shape({
    classes: PropTypes.shape({
      wrapper: PropTypes.string,
      icon: PropTypes.string
    }),
    iconSize: PropTypes.string
  })
};
